import React, { useState } from "react";
import toast from "react-hot-toast";
import { searchBackups, restoreBackupRecord, deleteExpiredBackups } from "../services/backupService";
import { writeAuditLog } from "../services/auditLogService";
import { useFirestoreAdmin } from "../context/FirestoreAdminProvider";
import { serializeValue } from "../utils/firestoreValueUtils";

export default function RestoreCenterPage() {
  const { user } = useFirestoreAdmin();
  const [collection, setCollection] = useState("");
  const [docId, setDocId] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [selected, setSelected] = useState(() => new Set());

  const search = async () => {
    setLoading(true);
    try {
      const data = await searchBackups({ collection: collection.trim(), documentId: docId.trim() });
      setRows(data);
    } catch (e) {
      toast.error(e?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const restoreSelected = async () => {
    const records = rows.filter((r) => selected.has(r.id));
    if (!records.length) return;
    if (window.prompt(`Type RESTORE to restore ${records.length} backup(s)`) !== "RESTORE") return;
    setLoading(true);
    try {
      for (const rec of records) {
        await restoreBackupRecord(rec);
      }
      await writeAuditLog({
        adminName: user?.email,
        adminUid: user?.uid,
        action: "restore_backup",
        collection: records[0]?.collection,
        documentIds: records.map((r) => r.firestoreDocId),
      });
      toast.success(`Restored ${records.length} document(s)`);
      await search();
    } catch (e) {
      toast.error(e?.message || "Restore failed");
    } finally {
      setLoading(false);
    }
  };

  const purgeExpired = async () => {
    const n = await deleteExpiredBackups();
    toast.success(`Removed ${n} expired backup(s)`);
    await search();
  };

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-bold">Restore Center</h1>
        <p className="text-sm text-slate-600">Search backups in <code>firestore_backups</code>, preview JSON, and restore.</p>
      </header>
      <div className="fa-card flex flex-wrap gap-2 p-4">
        <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" placeholder="Collection" value={collection} onChange={(e) => setCollection(e.target.value)} />
        <input className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm" placeholder="Document ID" value={docId} onChange={(e) => setDocId(e.target.value)} />
        <button type="button" className="fa-btn fa-btn-primary" onClick={search} disabled={loading}>Search</button>
        <button type="button" className="fa-btn" onClick={restoreSelected} disabled={!selected.size}>Restore selected</button>
        <button type="button" className="fa-btn fa-btn-danger" onClick={purgeExpired}>Delete expired</button>
      </div>
      <div className="fa-card fa-table-wrap">
        <table className="fa-table">
          <thead>
            <tr>
              <th />
              <th>Collection</th>
              <th>Doc ID</th>
              <th>Operation</th>
              <th>Created</th>
              <th>Preview</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() =>
                      setSelected((prev) => {
                        const next = new Set(prev);
                        if (next.has(r.id)) next.delete(r.id);
                        else next.add(r.id);
                        return next;
                      })
                    }
                  />
                </td>
                <td>{r.collection}</td>
                <td className="font-mono text-xs">{r.firestoreDocId}</td>
                <td>{r.operation}</td>
                <td className="text-xs">{r.createdAt}</td>
                <td>
                  <button type="button" className="fa-btn" onClick={() => setPreview(r)}>JSON</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {preview && (
        <div className="fa-modal-backdrop" onClick={() => setPreview(null)}>
          <div className="fa-modal" onClick={(e) => e.stopPropagation()}>
            <div className="border-b p-4 font-semibold">Backup preview — {preview.firestoreDocId}</div>
            <pre className="max-h-[60vh] overflow-auto p-4 text-xs">
              {JSON.stringify(serializeValue(preview.data), null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
}
